import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { STANDARD_PACKAGES } from "../mock";
import { createBooking } from "../lib/api";

const PREFERRED = ["Morning", "Lunchtime", "Evening", "Weekend"];

export default function BookPage() {
  const [params] = useSearchParams();
  const presetPackage = params.get("package");
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred: "Morning",
    interest: presetPackage || "",
    goals: "",
  });

  useEffect(() => {
    if (presetPackage) setForm((f) => ({ ...f, interest: presetPackage }));
  }, [presetPackage]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await createBooking({
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        preferred: form.preferred,
        interest: form.interest || null,
        goals: form.goals || null,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Please check the form and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="pt-[120px] lg:pt-[160px] pb-24 bg-[var(--lc-cream)] min-h-screen">
        <div className="mx-auto max-w-[720px] px-5 lg:px-10 text-center">
          <div className="w-16 h-16 rounded-full bg-[var(--lc-moss)] grid place-items-center mx-auto mb-6">
            <CheckCircle2 className="h-7 w-7 text-white" />
          </div>
          <div className="eyebrow text-[var(--lc-moss)] mb-4">Booking received</div>
          <h1 className="font-serif-display text-[clamp(36px,5vw,68px)] leading-[1.05] font-light">
            Thank you, {form.name.split(" ")[0]}.<br />
            <span className="italic">We'll be in touch shortly.</span>
          </h1>
          <p className="mt-7 text-[15px] leading-relaxed text-[var(--lc-muted)]">
            One of our coaches will reach out within 24 hours at <span className="text-[var(--lc-ink)] font-semibold">{form.email}</span> to confirm your free 30-minute discovery call.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors"
          >
            Back to Home <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-[120px] lg:pt-[150px] pb-24 bg-[var(--lc-cream)]">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="eyebrow text-[var(--lc-moss)] mb-5">Free 1:1 Call</div>
            <h1 className="font-serif-display text-[clamp(38px,5.5vw,72px)] leading-[1] font-light">
              Book your <span className="italic">discovery call.</span>
            </h1>
            <p className="mt-6 text-[15px] leading-relaxed text-[var(--lc-muted)] max-w-md">
              30 minutes. No commitment. We’ll review your goals, schedule and
              budget — then recommend the right pillars and package to start.
            </p>
            <div className="mt-9 space-y-4 text-sm">
              {[
                "Goal & lifestyle review",
                "Pillar recommendations",
                "Custom quote within 24h",
              ].map((b) => (
                <div key={b} className="flex items-center gap-3">
                  <CheckCircle2 className="h-4 w-4 text-[var(--lc-moss)]" />
                  {b}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bg-white border border-[var(--lc-line)] rounded-md p-6 lg:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" required value={form.name} onChange={(v) => update("name", v)} />
                <Field label="Email" type="email" required value={form.email} onChange={(v) => update("email", v)} />
              </div>
              <Field label="Phone / WhatsApp" value={form.phone} onChange={(v) => update("phone", v)} />

              <div>
                <label className="eyebrow text-[var(--lc-muted)] mb-2 block">Preferred time</label>
                <div className="flex flex-wrap gap-2">
                  {PREFERRED.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => update("preferred", p)}
                      className={`px-4 py-2 text-[12px] font-semibold tracking-[0.14em] uppercase border transition-colors ${
                        form.preferred === p
                          ? "bg-[var(--lc-ink)] text-[var(--lc-cream)] border-[var(--lc-ink)]"
                          : "bg-white text-[var(--lc-ink)] border-[var(--lc-line)] hover:border-[var(--lc-ink)]"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="eyebrow text-[var(--lc-muted)] mb-2 block">Interested in</label>
                <select
                  value={form.interest}
                  onChange={(e) => update("interest", e.target.value)}
                  className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[14px] focus:outline-none focus:border-[var(--lc-ink)]"
                >
                  <option value="">Not sure yet</option>
                  {STANDARD_PACKAGES.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                  <option value="custom">Custom — Build my own plan</option>
                </select>
              </div>

              <div>
                <label className="eyebrow text-[var(--lc-muted)] mb-2 block">Your goals (optional)</label>
                <textarea
                  rows={4}
                  value={form.goals}
                  onChange={(e) => update("goals", e.target.value)}
                  placeholder="Lose 10 kg, run a half-marathon, optimize bloodwork..."
                  className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[14px] focus:outline-none focus:border-[var(--lc-ink)] resize-none"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="group flex items-center justify-center gap-2 w-full px-6 py-4 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-[var(--lc-moss-dark)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Book My Free Call
                    <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </>
                )}
              </button>
              <p className="text-[11px] text-center text-[var(--lc-muted)] tracking-wider">
                By submitting you agree to be contacted by our team. No spam, ever.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="eyebrow text-[var(--lc-muted)] mb-2 block">{label}{required && " *"}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[14px] focus:outline-none focus:border-[var(--lc-ink)]"
      />
    </div>
  );
}
